import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import api from '../../services/api';

let taxonomyRequest;

const loadTaxonomy = () => {
  if (!taxonomyRequest) {
    taxonomyRequest = api.get('/cases/meta/analytics-taxonomy')
      .then(({ data }) => data)
      .catch((error) => {
        taxonomyRequest = undefined;
        throw error;
      });
  }
  return taxonomyRequest;
};

const readableCode = (value) => {
  const words = (value || '').toString().trim().replace(/[_-]+/g, ' ').toLowerCase();
  return words ? `${words.charAt(0).toUpperCase()}${words.slice(1)}` : '';
};

const asOption = (entry, kind) => ({
  code: entry.code,
  label: entry.label,
  kind,
});

const AnalyticsTagsField = ({
  domainCodes = [],
  competencyCodes = [],
  onChange,
  helperText,
  customKind = 'competency',
  sx,
}) => {
  const [taxonomy, setTaxonomy] = useState({ domains: [], competencies: [] });

  useEffect(() => {
    let active = true;
    loadTaxonomy()
      .then((result) => {
        if (active) setTaxonomy(result || { domains: [], competencies: [] });
      })
      .catch(() => {
        // Free-entry remains available if taxonomy suggestions cannot be loaded.
      });
    return () => {
      active = false;
    };
  }, []);

  const options = useMemo(() => [
    ...(taxonomy.domains || []).map((entry) => asOption(entry, 'domain')),
    ...(taxonomy.competencies || []).map((entry) => asOption(entry, 'competency')),
  ], [taxonomy]);

  const selected = useMemo(() => {
    const optionByCode = new Map(options.map((option) => [option.code, option]));
    return [
      ...domainCodes.map((code) => optionByCode.get(code) || {
        code,
        label: readableCode(code),
        kind: 'domain',
      }),
      ...competencyCodes.map((code) => optionByCode.get(code) || {
        code,
        label: readableCode(code),
        kind: 'competency',
      }),
    ];
  }, [competencyCodes, domainCodes, options]);

  const handleChange = (_event, values) => {
    const next = { domainCodes: [], competencyCodes: [] };
    values.forEach((value) => {
      const option = typeof value === 'string'
        ? { code: value.trim(), kind: customKind }
        : value;
      if (!option?.code) return;
      const target = option.kind === 'domain' ? next.domainCodes : next.competencyCodes;
      if (!target.includes(option.code)) target.push(option.code);
    });
    onChange(next);
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      filterSelectedOptions
      options={options}
      value={selected}
      groupBy={(option) => (option.kind === 'domain' ? 'Broad domains' : 'Specific competencies')}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || readableCode(option.code))}
      isOptionEqualToValue={(option, value) => option.code === value.code && option.kind === value.kind}
      onChange={handleChange}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label="Analytics tags (optional)"
          helperText={helperText}
          placeholder={selected.length ? '' : 'Choose a suggestion or type a custom tag'}
          sx={sx}
        />
      )}
    />
  );
};

export default AnalyticsTagsField;

export const _resetTaxonomyCacheForTests = () => {
  taxonomyRequest = undefined;
};
